import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { 
  ArrowUpRight, ArrowRight, Download, ChevronDown, TrendingUp, TrendingDown,
  Users, Church, Baby, Heart, Droplets, HandHeart, Building2, MapPin
} from 'lucide-react';
import { financialAPI, storiesAPI, companyAPI, attendanceAPI } from '../services/api';

// ============================================================================
// Reusable Section Background with Parallax + Ken Burns + Smooth Edges
// ============================================================================
const SectionBackground = ({ image, opacity = 0.6, objectPosition = 'center', edges = 'both' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Parallax: Bild bewegt sich langsamer als die Section
  const y = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  // Ken Burns: subtle Zoom während Section sichtbar
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1.05, 1.15]);
  // Fade an Rändern für nahtlosen Übergang
  const imageOpacity = useTransform(
    scrollYProgress,
    [0, 0.15, 0.85, 1],
    [0, opacity, opacity, 0]
  );

  return (
    <div ref={ref} className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
      <motion.div 
        style={{ y, scale, opacity: imageOpacity }}
        className="absolute inset-[-10%]"
      >
        <img
          src={image}
          alt=""
          className="w-full h-full object-cover"
          style={{ objectPosition }}
        />
      </motion.div>
      
      {/* Top fade for seamless blend with previous section */}
      {(edges === 'both' || edges === 'top') && (
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/60 to-transparent"></div>
      )}
      
      {/* Bottom fade for seamless blend with next section */}
      {(edges === 'both' || edges === 'bottom') && (
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
      )}
      
      {/* Center vignette for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
    </div>
  );
};

// ============================================================================
// Animated Counter mit Scroll-Trigger
// ============================================================================
const ScrollCounter = ({ end, duration = 2000, decimals = 0, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime;
    let frame;
    const animate = (now) => {
      if (!startTime) startTime = now;
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(eased * end);
      if (progress < 1) frame = requestAnimationFrame(animate);
      else setCount(end);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString('de-DE', { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      })}{suffix}
    </span>
  );
};

// ============================================================================
// Side Progress Indicator
// ============================================================================
const ProgressIndicator = ({ acts, activeAct }) => (
  <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-3">
    {acts.map((act, i) => (
      <div key={i} className="group relative flex items-center gap-3">
        <span className={`text-xs font-bold uppercase tracking-widest transition-all duration-500 ${
          activeAct === i ? 'text-[#FF4600] opacity-100' : 'text-white/40 opacity-0 group-hover:opacity-100'
        }`}>
          {act}
        </span>
        <div className={`h-px transition-all duration-500 ${
          activeAct === i ? 'w-12 bg-[#FF4600]' : 'w-6 bg-white/30'
        }`}></div>
      </div>
    ))}
  </div>
);

// ============================================================================
// ACT 1: Hero / Intro - Cinematic Sticky Transition
// ============================================================================
const ActIntro = ({ year, companyName }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  // Image transforms - scale down and fade
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.6, 0.15]);
  const imageBlur = useTransform(scrollYProgress, [0.4, 1], [0, 8]);
  
  // Dark overlay grows
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.85]);
  
  // Title emerges
  const titleOpacity = useTransform(scrollYProgress, [0.35, 0.7], [0, 1]);
  const titleY = useTransform(scrollYProgress, [0.35, 1], [60, -20]);
  const titleScale = useTransform(scrollYProgress, [0.35, 0.8], [0.85, 1]);
  
  // Glow pulses with scroll
  const glowOpacity = useTransform(scrollYProgress, [0.3, 0.7, 1], [0, 0.6, 0.3]);
  const glowScale = useTransform(scrollYProgress, [0.3, 1], [0.5, 1.5]);

  // Subtitle emerges later
  const subtitleOpacity = useTransform(scrollYProgress, [0.6, 0.85], [0, 1]);
  const subtitleY = useTransform(scrollYProgress, [0.6, 1], [40, 0]);

  // Scroll indicator at bottom of section
  const scrollHintOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [1, 1, 0, 0]);

  return (
    <section ref={ref} className="relative" style={{ height: '150vh' }} data-testid="hero-section">
      <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden bg-black">
        {/* The Image */}
        <motion.div
          style={{ 
            scale: imageScale, 
            opacity: imageOpacity,
            filter: useTransform(imageBlur, b => `blur(${b}px)`)
          }}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-none"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/oxycnbu4_Hoffnungstra%CC%88ger%20-%20Header.png"
            alt="Hoffnungsträger 2025"
            className="w-full h-auto block"
            data-testid="hoffnungstraeger-logo"
          />
        </motion.div>

        {/* Dark overlay that grows on scroll */}
        <motion.div 
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-black pointer-events-none"
        />

        {/* Orange glow behind title */}
        <motion.div
          style={{ 
            opacity: glowOpacity,
            scale: glowScale
          }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <div className="w-[60%] h-[60%] bg-[radial-gradient(circle,rgba(255,70,0,0.35),transparent_70%)]" />
        </motion.div>

        {/* Emerging title */}
        <motion.div
          style={{ 
            opacity: titleOpacity, 
            y: titleY,
            scale: titleScale
          }}
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
        >
          <motion.div
            style={{ opacity: titleOpacity }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="h-px w-12 bg-[#FF4600]"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-[0.4em] text-xs md:text-sm">
              Vorwort
            </span>
            <div className="h-px w-12 bg-[#FF4600]"></div>
          </motion.div>

          <h2 className="text-6xl md:text-8xl lg:text-[10rem] font-black text-white uppercase tracking-tight leading-[0.9]">
            Was für ein<br/>
            <span className="text-[#FF4600]" style={{
              textShadow: '0 0 60px rgba(255, 70, 0, 0.5)'
            }}>Jahr.</span>
          </h2>

          <motion.p
            style={{ opacity: subtitleOpacity, y: subtitleY }}
            className="text-lg md:text-2xl text-white/70 max-w-2xl mx-auto mt-8 font-light italic"
          >
            Eine Geschichte über Wachstum, Vertrauen und die Kraft der Hoffnung.
          </motion.p>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          style={{ opacity: scrollHintOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 z-10"
        >
          <span className="text-xs uppercase tracking-widest font-bold">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// ACT 1.5: Vorwort - Text emerges naturally after the cinematic intro
// ============================================================================
const ActForeword = () => {
  return (
    <section className="relative pt-32 md:pt-48 pb-32 md:pb-48 bg-black overflow-hidden">
      {/* Background image - placeholder, awaiting image 1 */}

      {/* Subtle accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[40%] opacity-10 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,70,0,0.3),transparent_70%)]" />
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        {/* Paragraphs - 2-column layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 text-lg md:text-xl text-white/80 font-light leading-relaxed">
          {/* Left column */}
          <div className="space-y-8">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-white font-bold">„Hoffnungsträger"</span> war unser Wort für 2025 – 
              nicht nur als Motto, sondern als Einladung. Eine Einladung, Hoffnung nicht nur zu haben, 
              sondern sie zu leben und weiterzugeben. Wenn wir jetzt auf dieses Jahr zurückschauen, 
              dann mit echter Dankbarkeit: <span className="text-[#FF4600] font-bold">Gott hat gehandelt</span> – 
              auf eine Weise, die uns immer wieder überrascht hat.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Nach vielen Jahren der Suche haben wir ein <span className="text-white font-bold">Gebäude</span> gefunden, 
              das ein Zuhause für das werden kann, was Gott in dieser Stadt tut. Wir haben ein 
              <span className="text-white font-bold"> College gestartet</span>, weil wir glauben, dass die nächste 
              Erweckung im Norden Menschen braucht, die dafür ausgerüstet sind. Wir haben gemeinsam mit fünf 
              anderen Gemeinden <span className="text-white font-bold">über 700 Menschen zu einem Taufgottesdienst</span> unter 
              freiem Himmel versammelt – ein Moment, den viele so schnell nicht vergessen werden. Und die 
              nächste Generation? Die hat in diesem Jahr und besonders beim 
              <span className="text-white font-bold"> Kidscamp und Powerup</span> ihre ganz eigenen Begegnungen 
              mit Gott gemacht. Da wächst eine kraftvolle Generation heran.
            </motion.p>
          </div>

          {/* Right column */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="border-l-4 border-[#FF4600] pl-6 md:pl-8 py-2"
            >
              <p className="text-white">
                Dann gibt es Geschichten wie die von <span className="text-[#FF4600] font-bold">Eva</span>. 
                Ihr Herz war <span className="italic">„völlig kaputt und zerschlagen"</span>, als Jesus Christus 
                sie fand. Was in dem Moment geschah, veränderte alles – Vergebung, ein neuer Lebensweg, ein 
                geheiltes Herz. <span className="italic text-white/90">„Wie es sonst nichts und niemand anderes auf dieser Welt hätte tun können"</span>, 
                sagt sie selbst. Evas Geschichte steht stellvertretend für viele, deren Leben in diesem Jahr 
                in unserer Gemeinde von innen heraus verändert wurde. <span className="text-[#FF4600] font-bold">Das ist der Kern von allem.</span>
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Hinter all dem stehen Menschen – <span className="text-[#FF4600] font-bold">ihr</span>. 
              Eure Treue, eure Mitarbeit, euer finanzielles Mittragen. Diese Gemeinde lebt davon, dass 
              so viele von euch nicht nur dabei sind, sondern wirklich mitmachen. 
              <span className="text-white font-bold"> Dafür sind wir von Herzen dankbar.</span>
            </motion.p>
          </div>
        </div>

        {/* Closing statement */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-2xl md:text-3xl lg:text-4xl text-white font-light italic mt-20 max-w-5xl"
        >
          Dieser Report ist unser Versuch, das festzuhalten – nicht als Rechenschaftsbericht, 
          sondern als <span className="text-[#FF4600] font-bold not-italic">Einladung</span>, 
          gemeinsam innezuhalten und zu staunen, was Gott in diesem Jahr getan hat.
        </motion.p>

        {/* Bottom decoration */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mt-20 h-px bg-gradient-to-r from-transparent via-[#FF4600] to-transparent origin-center"
        />
      </div>
    </section>
  );
};

// ============================================================================
// ACT 2: The Big Number - 19.299 with sticky reveal
// ============================================================================
const ActBigNumber = ({ totalAttendees, services }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const numberScale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.8, 1, 1, 0.9]);
  const textOpacity1 = useTransform(scrollYProgress, [0.1, 0.25], [0, 1]);
  const textOpacity2 = useTransform(scrollYProgress, [0.4, 0.55], [0, 1]);
  const textOpacity3 = useTransform(scrollYProgress, [0.7, 0.85], [0, 1]);

  return (
    <section ref={ref} className="relative" style={{ height: '300vh' }}>
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {/* Background image - Bild 2 */}
        <SectionBackground 
          image="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/2dp4gdsf_2.png"
          opacity={0.7}
        />

        <div className="text-center px-6 relative w-full max-w-7xl mx-auto z-10">
          <motion.p 
            style={{ opacity: textOpacity1 }}
            className="text-[#FF4600] font-bold uppercase tracking-[0.4em] text-sm mb-8"
          >
            Beginnen wir mit einer Zahl
          </motion.p>

          <motion.h2 
            style={{ scale: numberScale }}
            className="text-[6rem] sm:text-[9rem] md:text-[18rem] lg:text-[22rem] font-black text-white leading-none tracking-tighter"
          >
            <ScrollCounter end={totalAttendees} duration={3000} />
          </motion.h2>

          <motion.p 
            style={{ opacity: textOpacity2 }}
            className="text-3xl md:text-5xl font-black text-white uppercase mt-8 tracking-tight"
          >
            Menschen haben mit uns <span className="text-[#FF4600]">gefeiert</span>.
          </motion.p>

          <motion.p 
            style={{ opacity: textOpacity3 }}
            className="text-xl md:text-2xl text-white/60 mt-6 font-light"
          >
            In <span className="text-[#FF4600] font-bold">{services} Gottesdiensten</span> an drei Standorten.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// ACT 3: Locations Reveal
// ============================================================================
const ActLocations = ({ locations }) => {
  const ref = useRef(null);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center py-32 px-6 bg-black overflow-hidden">
      {/* Background image - Bild 3 */}
      <SectionBackground 
        image="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/uw0x4yk0_3.jpg"
        opacity={0.65}
        objectPosition="center 30%"
      />

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#FF4600]"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
              Drei Standorte
            </span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tight leading-none">
            Eine <span className="text-[#FF4600]">Familie</span>.
          </h2>
        </motion.div>

        <div className="space-y-2">
          {locations.map((loc, i) => (
            <motion.div
              key={loc.name}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              className="group border-b border-white/10 py-8 md:py-12 grid grid-cols-12 gap-4 md:gap-6 items-center hover:bg-white/5 transition-colors px-4"
            >
              <div className="col-span-12 md:col-span-1">
                <span className="text-[#FF4600] font-black text-2xl md:text-3xl">0{i + 1}</span>
              </div>
              <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                <MapPin className="w-6 h-6 text-[#FF4600]" strokeWidth={2.5} />
                <span className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight">
                  {loc.name}
                </span>
              </div>
              <div className="col-span-6 md:col-span-3">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Gottesdienste</p>
                <p className="text-2xl md:text-3xl font-black text-white">
                  <ScrollCounter end={loc.services} duration={1500} />
                </p>
              </div>
              <div className="col-span-6 md:col-span-4">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Teilnehmer</p>
                <p className="text-2xl md:text-4xl font-black text-[#FF4600]">
                  <ScrollCounter end={loc.total_attendees} duration={2000} />
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// ACT 4: Detail Metrics - Sequential Reveal
// ============================================================================
const ActDetailMetrics = ({ total }) => {
  const [expanded, setExpanded] = useState(false);

  const primaryMetrics = [
    { value: total.kids, label: 'Kids', sublabel: 'in der KidsChurch', icon: Baby },
    { value: total.first_time_visitors, label: 'Erstbesucher', sublabel: 'das erste Mal dabei', icon: Users },
    { value: total.decisions_for_jesus, label: 'Entscheidungen', sublabel: 'für Jesus', icon: Heart },
    { value: total.baptisms, label: 'Taufen', sublabel: 'haben wir gefeiert', icon: Droplets },
  ];

  const secondaryMetrics = [
    { value: total.visitors, label: 'Besucher', sublabel: 'gesamt im Saal', icon: Users },
    { value: total.team, label: 'Team', sublabel: 'ehrenamtliche Einsätze', icon: HandHeart },
    { value: total.online, label: 'Online', sublabel: 'via Livestream', icon: Church },
    { value: total.ministry_prayer, label: 'Gebete', sublabel: 'im Ministry-Team', icon: Heart },
    { value: total.fixed_team_members, label: 'Mitarbeiter', sublabel: 'fest im Team', icon: HandHeart },
    { value: total.group_participants, label: 'Kleingruppen', sublabel: 'Teilnehmer', icon: Users },
    { value: total.avg_per_service, label: 'Aktive', sublabel: 'pro Gottesdienst', icon: Users },
  ].filter(m => m.value && m.value > 0);

  const renderCard = (metric, i, delayBase = 0) => {
    const Icon = metric.icon;
    return (
      <motion.div
        key={`${metric.label}-${i}`}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.7, delay: delayBase + i * 0.1 }}
        className="bg-white/5 border border-white/10 p-8 hover:border-[#FF4600] hover:bg-[#FF4600]/10 transition-all group"
      >
        <Icon className="w-10 h-10 text-[#FF4600] mb-6" strokeWidth={2} />
        <p className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-none tracking-tight whitespace-nowrap">
          <ScrollCounter end={metric.value} duration={2000} />
        </p>
        <p className="text-[#FF4600] font-black uppercase tracking-widest text-sm mt-4">
          {metric.label}
        </p>
        <p className="text-white/40 text-sm mt-1 font-light">
          {metric.sublabel}
        </p>
      </motion.div>
    );
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center py-32 px-6 bg-black overflow-hidden">
      {/* Background image - Bild 4 */}
      <SectionBackground 
        image="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/kqa3rmvh_4.jpg"
        opacity={0.65}
      />

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <p className="text-[#FF4600] font-bold uppercase tracking-[0.4em] text-sm mb-6">
            Hinter jeder Zahl
          </p>
          <h2 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tight leading-none max-w-4xl">
            steht ein <span className="text-[#FF4600]">Mensch</span>.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {primaryMetrics.map((m, i) => renderCard(m, i))}
        </div>

        {/* Expandable secondary metrics */}
        <AnimatePresence>
          {expanded && secondaryMetrics.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {secondaryMetrics.map((m, i) => renderCard(m, i, 0))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expand / Collapse button */}
        {secondaryMetrics.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 flex justify-center"
          >
            <button
              onClick={() => setExpanded(!expanded)}
              data-testid="toggle-all-metrics-btn"
              className="group inline-flex items-center gap-3 bg-transparent border-2 border-[#FF4600] hover:bg-[#FF4600] text-[#FF4600] hover:text-white px-8 py-4 font-black uppercase tracking-widest text-sm transition-all"
            >
              {expanded ? 'Weniger anzeigen' : 'Alle Zahlen anzeigen'}
              <motion.span
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="inline-flex"
              >
                <ChevronDown className="w-5 h-5" strokeWidth={3} />
              </motion.span>
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// ============================================================================
// ACT 5: Transition Scene
// ============================================================================
const ActTransition = ({ text, highlight, backgroundImage, children }) => {
  return (
    <section className="h-screen flex items-center justify-center px-6 bg-black relative overflow-hidden">
      {backgroundImage && (
        <SectionBackground image={backgroundImage} opacity={0.7} />
      )}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1 }}
        className="text-center max-w-5xl relative z-10"
      >
        <p className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight">
          {children ? children : (
            <>
              {text}{' '}
              <span className="text-[#FF4600]">{highlight}</span>.
            </>
          )}
        </p>
      </motion.div>
    </section>
  );
};

// ============================================================================
// ACT 6: Financial Trend - Sticky Chart
// ============================================================================
const ActFinancialTrend = ({ historicalData }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Animations triggered by scroll progress
  const sorted = [...historicalData].sort((a, b) => a.year - b.year);
  const maxRevenue = Math.max(...sorted.map(d => d.revenue));
  const firstYear = sorted[0];
  const lastYear = sorted[sorted.length - 1];
  const growthPercent = ((lastYear.revenue - firstYear.revenue) / firstYear.revenue * 100).toFixed(1);

  // Bar heights based on scroll
  const bar1Height = useTransform(scrollYProgress, [0.15, 0.3], [0, (sorted[0].revenue / maxRevenue) * 100]);
  const bar2Height = useTransform(scrollYProgress, [0.3, 0.45], [0, (sorted[1].revenue / maxRevenue) * 100]);
  const bar3Height = useTransform(scrollYProgress, [0.45, 0.6], [0, ((sorted[2].revenue - (sorted[2].special_donations || 0)) / maxRevenue) * 100]);
  const specialBarHeight = useTransform(scrollYProgress, [0.65, 0.78], [0, ((sorted[2].special_donations || 0) / maxRevenue) * 100]);

  const titleOpacity = useTransform(scrollYProgress, [0, 0.1], [0, 1]);
  const specialNoteOpacity = useTransform(scrollYProgress, [0.7, 0.82], [0, 1]);
  const conclusionOpacity = useTransform(scrollYProgress, [0.85, 0.95], [0, 1]);

  return (
    <section ref={ref} className="relative" style={{ height: '500vh' }}>
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 relative z-10">
          <motion.div style={{ opacity: titleOpacity }} className="mb-8 md:mb-12">
            <div className="flex items-start justify-between gap-4 sm:gap-6 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-3 md:mb-4">
                  <div className="h-px w-8 sm:w-12 bg-[#FF4600] flex-shrink-0"></div>
                  <span className="text-[#FF4600] font-bold uppercase tracking-widest text-[10px] sm:text-sm">
                    Die finanzielle Reise
                  </span>
                </div>
                <h2 className="text-[2rem] leading-[0.95] sm:text-4xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight">
                  Der <span className="text-[#FF4600]">Trend</span><br className="sm:hidden"/> ist klar
                </h2>
              </div>
              <motion.img
                initial={{ opacity: 0, rotate: -8, scale: 0.85 }}
                whileInView={{ opacity: 1, rotate: -3, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                src="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/fg8nzf56_6.png"
                alt="Mein Herz sein Haus"
                className="w-20 sm:w-32 md:w-48 lg:w-56 h-auto flex-shrink-0"
                style={{ filter: 'drop-shadow(0 8px 24px rgba(255, 70, 0, 0.25))' }}
                data-testid="trend-icon-logo"
              />
            </div>
          </motion.div>

          {/* Chart */}
          <div className="bg-white/5 border border-white/10 p-4 sm:p-8 md:p-12 relative">
            {/* Legend / Label */}
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="h-px w-6 sm:w-8 bg-white/40 flex-shrink-0"></div>
              <span className="text-white/60 font-bold uppercase tracking-widest text-[10px] sm:text-xs">
                Einnahmen pro Jahr
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-6 md:gap-12 items-end" style={{ height: '45vh' }}>
              {/* 2023 Bar */}
              <div className="flex flex-col items-center h-full justify-end">
                <motion.div className="w-full max-w-[200px] mx-auto bg-gradient-to-t from-white/20 to-white/10 relative"
                  style={{ height: useTransform(bar1Height, v => `${v}%`) }}>
                  <motion.div 
                    className="absolute -top-7 sm:-top-10 left-1/2 -translate-x-1/2 text-[10px] sm:text-lg md:text-2xl font-black text-white whitespace-nowrap"
                    style={{ opacity: useTransform(scrollYProgress, [0.2, 0.3], [0, 1]) }}
                  >
                    {sorted[0].revenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                  </motion.div>
                </motion.div>
                <p className="text-base sm:text-2xl md:text-3xl font-black text-white/60 mt-3 sm:mt-4">{sorted[0].year}</p>
              </div>

              {/* 2024 Bar */}
              <div className="flex flex-col items-center h-full justify-end">
                <motion.div className="w-full max-w-[200px] mx-auto bg-gradient-to-t from-white/20 to-white/10 relative"
                  style={{ height: useTransform(bar2Height, v => `${v}%`) }}>
                  <motion.div 
                    className="absolute -top-7 sm:-top-10 left-1/2 -translate-x-1/2 text-[10px] sm:text-lg md:text-2xl font-black text-white whitespace-nowrap"
                    style={{ opacity: useTransform(scrollYProgress, [0.35, 0.45], [0, 1]) }}
                  >
                    {sorted[1].revenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                  </motion.div>
                  <motion.div 
                    className="absolute -top-14 sm:-top-20 left-1/2 -translate-x-1/2 bg-[#FF4600] text-white text-[9px] sm:text-xs font-black uppercase tracking-widest px-2 sm:px-3 py-0.5 sm:py-1 whitespace-nowrap"
                    style={{ opacity: useTransform(scrollYProgress, [0.4, 0.5], [0, 1]) }}
                  >
                    +11.8%
                  </motion.div>
                </motion.div>
                <p className="text-base sm:text-2xl md:text-3xl font-black text-white/60 mt-3 sm:mt-4">{sorted[1].year}</p>
              </div>

              {/* 2025 Bar - with special donations */}
              <div className="flex flex-col items-center h-full justify-end">
                <div className="w-full max-w-[200px] mx-auto relative h-full flex flex-col justify-end">
                  {/* Special donations on top */}
                  <motion.div 
                    className="w-full relative overflow-hidden"
                    style={{ 
                      height: useTransform(specialBarHeight, v => `${v}%`),
                      background: 'linear-gradient(to top, #FFA500, #FFD700)'
                    }}
                  >
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.15) 4px, rgba(0,0,0,0.15) 8px)'
                    }}></div>
                    <Building2 className="absolute inset-0 m-auto w-6 h-6 text-black/40" strokeWidth={2.5} />
                  </motion.div>
                  {/* Regular revenue */}
                  <motion.div 
                    className="w-full bg-gradient-to-t from-[#FF4600] to-[#ff6b2c] relative"
                    style={{ height: useTransform(bar3Height, v => `${v}%`) }}
                  >
                    <motion.div 
                      className="absolute -top-7 sm:-top-10 left-1/2 -translate-x-1/2 text-[10px] sm:text-lg md:text-2xl font-black text-[#FF4600] whitespace-nowrap"
                      style={{ opacity: useTransform(scrollYProgress, [0.55, 0.65], [0, 1]) }}
                    >
                      {sorted[2].revenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                    </motion.div>
                    <motion.div 
                      className="absolute -top-14 sm:-top-20 left-1/2 -translate-x-1/2 bg-[#FF4600] text-white text-[9px] sm:text-xs font-black uppercase tracking-widest px-2 sm:px-3 py-0.5 sm:py-1 whitespace-nowrap"
                      style={{ opacity: useTransform(scrollYProgress, [0.55, 0.65], [0, 1]) }}
                    >
                      +44.6%
                    </motion.div>
                  </motion.div>
                </div>
                <p className="text-base sm:text-2xl md:text-3xl font-black text-[#FF4600] mt-3 sm:mt-4">{sorted[2].year}</p>
              </div>
            </div>

            {/* Special donations note */}
            <motion.div 
              style={{ opacity: specialNoteOpacity }}
              className="mt-8 p-4 md:p-6 bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-start gap-3"
            >
              <Building2 className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <p className="text-white text-sm md:text-base">
                <span className="text-[#FFD700] font-bold">140.000 €</span> sind einmalige Sonderspenden für das Gebäude.
                Organisches Wachstum: <span className="text-white font-bold">+29.2%</span>
              </p>
            </motion.div>
          </div>

          {/* Conclusion */}
          <motion.div 
            style={{ opacity: conclusionOpacity }}
            className="mt-6 sm:mt-8 text-center"
          >
            <p className="text-xl sm:text-2xl md:text-4xl font-black text-white leading-tight">
              Insgesamt <span className="text-[#FF4600]">+{growthPercent}%</span> seit {firstYear.year}.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// ACT 6.5: Year Result - Jahresergebnis mit Vorjahresvergleich
// ============================================================================
const ActYearResult = ({ historicalData }) => {
  const sorted = [...historicalData].sort((a, b) => a.year - b.year);

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    const formatted = absValue.toLocaleString('de-DE', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    });
    return `${value < 0 ? '−' : '+'}${formatted} €`;
  };

  return (
    <section className="min-h-screen flex items-center py-32 px-6 bg-black relative overflow-hidden" data-testid="year-result-section">
      <div className="max-w-7xl mx-auto w-full relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#FF4600]"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
              Jahresergebnis
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none">
            Unter dem Strich
          </h2>
          <p className="text-lg md:text-xl text-white/60 mt-6 max-w-3xl leading-relaxed">
            Einnahmen <span className="text-white/80">−</span> Ausgaben <span className="text-white/80">=</span> <span className="text-[#FF4600] font-bold">Ergebnis</span>
          </p>
        </motion.div>

        {/* Year cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {sorted.map((data, idx) => {
            const isPositive = data.result >= 0;
            const isLatest = idx === sorted.length - 1;
            const Icon = isPositive ? TrendingUp : TrendingDown;
            const colorClass = isPositive ? 'text-[#FF4600]' : 'text-white/40';
            const borderClass = isLatest 
              ? 'border-[#FF4600] bg-[#FF4600]/5' 
              : 'border-white/10 bg-white/[0.02]';

            return (
              <motion.div
                key={data.year}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: idx * 0.15 }}
                className={`relative border ${borderClass} p-8 md:p-10 transition-colors`}
                data-testid={`year-result-card-${data.year}`}
              >
                {isLatest && (
                  <div className="absolute -top-3 left-8">
                    <span className="bg-[#FF4600] text-white text-xs font-black uppercase tracking-widest px-3 py-1.5">
                      Aktuell
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-6">
                  <p className="text-3xl md:text-4xl font-black text-white/70">{data.year}</p>
                  <Icon 
                    className={`w-7 h-7 ${isPositive ? 'text-[#FF4600]' : 'text-white/30'}`} 
                    strokeWidth={2.5}
                  />
                </div>

                <p className={`text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-black leading-none tracking-tight ${isPositive ? 'text-[#FF4600]' : 'text-white'} break-words`}>
                  {formatCurrency(data.result)}
                </p>

                <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Einnahmen</span>
                    <span className="text-white/80 font-bold tabular-nums">
                      {data.revenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Ausgaben</span>
                    <span className="text-white/80 font-bold tabular-nums">
                      {data.expenses.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Vorläufige Zahlen Anmerkung */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 md:mt-12 text-center text-sm md:text-base text-white/70 italic font-light tracking-wide"
        >
          * Finanzzahlen für 2025 sind vorläufig und können sich nach Abschluss der Buchhaltung noch ändern.
        </motion.p>
      </div>
    </section>
  );
};

// ============================================================================
// ACT 7: Stories - Each story full screen
// ============================================================================
const ActStory = ({ story, index, total }) => {
  return (
    <section className="min-h-screen flex items-center py-32 px-6 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
            Highlight {index + 1} / {total}
          </span>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Image */}
          <motion.a
            href={story.link || undefined}
            target={story.link ? "_blank" : undefined}
            rel={story.link ? "noopener noreferrer" : undefined}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="relative aspect-[4/5] overflow-hidden group block order-1"
          >
            <img
              src={story.image}
              alt={story.title}
              style={{ objectPosition: story.image_position || 'center' }}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute top-6 left-6">
              <span className="bg-[#FF4600] text-white text-xs font-black uppercase tracking-widest px-3 py-1.5">
                {story.category}
              </span>
            </div>
            {story.link && (
              <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm p-3 group-hover:bg-[#FF4600] transition-colors">
                <ArrowUpRight className="w-6 h-6 text-white" strokeWidth={3} />
              </div>
            )}
          </motion.a>

          {/* Content */}
          <div className="order-2">
            <motion.p
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-[#FF4600] font-bold uppercase tracking-widest text-sm mb-4"
            >
              {story.date}
            </motion.p>
            
            <motion.h2
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none mb-8"
            >
              {story.title}
            </motion.h2>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="text-lg md:text-xl text-white/70 leading-relaxed font-light mb-8 space-y-4"
            >
              {story.description.split(/\n\n+/).map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </motion.div>

            {story.link && (
              <motion.a
                href={story.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="inline-flex items-center gap-3 bg-[#FF4600] hover:bg-[#ff5a1a] text-white px-8 py-4 font-black uppercase tracking-widest text-sm transition-colors"
              >
                Mehr erfahren
                <ArrowUpRight className="w-5 h-5" strokeWidth={3} />
              </motion.a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// ACT 8: Outro / Thank You
// ============================================================================
const ActOutro = ({ companyInfo, year }) => {
  return (
    <>
      {/* Scripture Anchor - Römer 4,17 */}
      <section className="py-32 md:py-40 px-6 bg-black relative overflow-hidden" data-testid="scripture-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-8 bg-[#FF4600]/60"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-[0.4em] text-xs">
              Römer 4,17
            </span>
            <div className="h-px w-8 bg-[#FF4600]/60"></div>
          </div>
          <blockquote className="text-white/80 text-xl md:text-2xl lg:text-3xl font-light italic leading-relaxed tracking-wide">
            <span className="text-[#FF4600] text-4xl md:text-5xl font-serif leading-none mr-1 align-top">„</span>
            Wie geschrieben steht: <span className="text-white">»Ich habe dich gesetzt zum Vater vieler Völker«</span> – vor Gott, dem er geglaubt hat, der die Toten lebendig macht und ruft das, was nicht ist, dass es sei.<span className="text-[#FF4600] text-4xl md:text-5xl font-serif leading-none ml-1 align-top">"</span>
          </blockquote>
        </motion.div>
      </section>

      {/* Outlook 2026 - Adventurous Faith */}
      <section className="min-h-screen flex items-center justify-center px-6 py-24 bg-black relative overflow-hidden" data-testid="outlook-2026-section">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-6xl mx-auto w-full text-center relative z-10"
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-8 bg-[#FF4600]/60"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-[0.4em] text-xs">
              Ausblick 2026
            </span>
            <div className="h-px w-8 bg-[#FF4600]/60"></div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-3xl mx-auto"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/dne886xm_CreativeNight%20Adventurous%20Faith.png"
              alt="Adventurous Faith 2026 – Glaube, der ins Abenteuer ruft"
              className="w-full h-auto"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-base md:text-lg text-white/60 mt-10 max-w-2xl mx-auto font-light leading-relaxed italic"
          >
            Ruf → Risiko → Reward. Wir gehen mit ins Abenteuer.
          </motion.p>
        </motion.div>
      </section>

      {/* Thank You Statement */}
      <section className="min-h-screen flex items-center justify-center px-6 bg-[#FF4600] relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="text-center max-w-5xl"
        >
          <p className="text-white/80 font-bold uppercase tracking-[0.4em] text-sm mb-12">
            Danke
          </p>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white uppercase tracking-tight leading-[0.95]">
            Jede Zahl<br/>
            ist eine <span className="text-black">Geschichte</span>.
          </h2>
          <p className="text-3xl md:text-5xl font-black text-white/80 mt-6 leading-tight">
            Jede Geschichte<br/>ist ein Zeugnis für die <span className="text-black">Güte Gottes</span>.
          </p>
        </motion.div>
      </section>

      {/* CTA + Footer */}
      <section className="py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-5xl md:text-6xl font-black text-white mb-8 uppercase tracking-tight leading-none"
          >
            <span className="text-[#FF4600]">Danke</span>, dass <span className="text-[#FF4600]">du</span><br/>
            ein Teil davon bist.
          </motion.h2>
          
          <div className="flex flex-wrap gap-4 justify-center mt-12">
            <a 
              href="https://eqfl.de/spenden"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#FF4600] hover:bg-[#ff5a1a] text-white px-8 py-5 font-black uppercase tracking-widest transition-colors"
            >
              <Heart className="w-5 h-5" strokeWidth={2.5} />
              Spenden
            </a>
            <a 
              href="https://eqfl.de/kontakt"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-transparent hover:bg-white hover:text-black text-white border-2 border-white px-8 py-5 font-black uppercase tracking-widest transition-colors"
            >
              Kontakt
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <h3 className="text-xl font-black text-white tracking-tight">{companyInfo?.name?.toUpperCase() || 'EQUIPPERS'}</h3>
            <ArrowUpRight className="w-4 h-4 text-[#FF4600]" strokeWidth={3} />
          </div>
          <p className="text-white/40 text-sm uppercase tracking-wider">
            © {year} · {companyInfo?.slogan || 'Kirche anders als du denkst.'}
          </p>
        </div>
      </footer>
    </>
  );
};

// ============================================================================
// MAIN: ScrollyReport
// ============================================================================
const ScrollyReport = () => {
  const [yearlyData, setYearlyData] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [stories, setStories] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeAct, setActiveAct] = useState(0);

  const acts = ['Intro', 'Vorwort', 'Die Zahl', 'Standorte', 'Menschen', 'Übergang', 'Finanzen', 'Highlights', 'Danke'];
  
  const scrollProgress = useScroll();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      const actIndex = Math.min(Math.floor(scrollPercent * acts.length), acts.length - 1);
      setActiveAct(actIndex);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadData = async () => {
    try {
      const [financial, historical, attendance, storiesRes, company] = await Promise.all([
        financialAPI.getLatest(),
        financialAPI.getAll(),
        attendanceAPI.getLatest(),
        storiesAPI.getAll(),
        companyAPI.get()
      ]);

      if (financial.success) setYearlyData(financial.data);
      if (historical.success) setHistoricalData(historical.data);
      if (attendance.success) setAttendanceData(attendance.data);
      if (storiesRes.success) setStories(storiesRes.data);
      if (company.success) setCompanyInfo(company.data);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  if (loading || !yearlyData || !attendanceData || historicalData.length < 2) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#FF4600] mx-auto mb-4"></div>
          <p className="text-white uppercase tracking-widest text-sm">Lade...</p>
        </div>
      </div>
    );
  }

  const total = attendanceData?.total || {};
  const locations = attendanceData?.locations || [];

  return (
    <div className="bg-black text-white" data-testid="scrolly-report">
      {/* Fixed Header */}
      <header className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-white/10 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-white tracking-tight">
              {companyInfo?.name?.toUpperCase() || 'EQUIPPERS'}
            </h1>
            <ArrowUpRight className="w-4 h-4 text-[#FF4600]" strokeWidth={3} />
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/report/pdf`}
              download
              data-testid="header-pdf-btn"
              title="Bericht als PDF herunterladen"
              className="hidden sm:inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[11px] font-bold uppercase tracking-widest px-3 py-2.5 border border-white/15 hover:border-white/40 transition-colors"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
              PDF
            </a>
            <a 
              href="https://eqfl.de/spenden"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-donate-btn"
              className="bg-[#FF4600] hover:bg-[#ff5a1a] text-white font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-colors"
            >
              Spenden
            </a>
          </div>
        </div>
      </header>

      <ProgressIndicator acts={acts} activeAct={activeAct} />

      {/* THE STORY */}
      <ActIntro year={yearlyData.year} companyName={companyInfo?.name} />
      
      <ActForeword />
      
      <ActBigNumber totalAttendees={total.total_attendees} services={total.services} />
      
      <ActLocations locations={locations} />
      
      <ActDetailMetrics total={total} />
      
      <ActTransition 
        text="Hinter Menschen stehen Entscheidungen. Hinter Entscheidungen steht" 
        highlight="Vertrauen" 
        backgroundImage="https://customer-assets.emergentagent.com/job_finanz-story-hub/artifacts/9i0oopze_5.jpg"
      />
      
      <ActFinancialTrend historicalData={historicalData} />
      
      <ActYearResult historicalData={historicalData} />
      
      <ActTransition>
        Was <span className="text-[#FF4600]">uns</span> in diesem Jahr <span className="text-[#FF4600]">bewegt</span> hat.
      </ActTransition>
      
      {stories.map((story, i) => (
        <ActStory key={story.id || story._id} story={story} index={i} total={stories.length} />
      ))}
      
      <ActOutro companyInfo={companyInfo} year={yearlyData.year} />
    </div>
  );
};

export default ScrollyReport;
